import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import Modal from './Modal';
import { OWNERSHIP_LABEL, holderFields, tagText } from '../lib/assets';
import { contractorAuth } from '../lib/contractorAuth';
import useUnsavedChanges, { UnsavedChangesDialog } from '../lib/useUnsavedChanges';
import CompanyChip from './asset-company/CompanyChip';
import AssetPicker from './AssetPicker';
import HolderSelect from './HolderSelect';
import './Assets.css';

/**
 * Adding a device, or changing what was typed about one.
 *
 * Gordon's decision is that IT types make, model and serial and nothing
 * else it does not have to - the heartbeat fills the technical fields later
 * - so those three are at the top and everything below "Purchase and
 * warranty" can be left empty.
 *
 * Adding also takes who has it and where, because entering a laptop that is
 * already on somebody's desk should be one form, not a form and then an
 * assignment. Editing does not: holder, place and status each have their own
 * action on the device's page, each with its own line in the history.
 *
 * The server says which field it refused and why; that sentence is shown
 * as it came.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const day = (v) => (v ? String(v).slice(0, 10) : '');

const blank = (meta) => ({
  asset_category_id: meta?.categories?.find((c) => c.active)?.id || '',
  company_id: '',
  asset_tag: '', manufacturer: '', model: '', model_number: '', serial_number: '',
  hostname: '', holder: '', location_id: '', default_location_id: '', at_home: false, is_shared: false,
  ownership: 'owned', department_id: '', purchase_date: '', supplier: '', invoice_ref: '',
  cost_ex_vat: '', currency: 'EUR', warranty_provider: '', warranty_ref: '',
  warranty_start: '', warranty_end: '', planned_refresh_date: '', notes: '', specs: {}
});

const fromAsset = (a) => ({
  ...a,
  purchase_date: day(a.purchase_date), warranty_start: day(a.warranty_start),
  warranty_end: day(a.warranty_end), planned_refresh_date: day(a.planned_refresh_date),
  cost_ex_vat: a.cost_ex_vat ?? '', specs: a.specs || {}, company_id: a.company_id || ''
});

// company_id is sent only when the register has companies (meta.companies),
// so a form on a database without 062 never names a field it has not got.
// ip_address to parent_asset_id (092) are sent too; a server without those
// columns simply does not read them (routes/assets.js fields()).
const TYPED = ['asset_category_id', 'company_id', 'asset_tag', 'manufacturer', 'model', 'model_number',
  'serial_number', 'hostname', 'is_shared', 'ownership', 'department_id', 'purchase_date',
  'supplier', 'invoice_ref', 'cost_ex_vat', 'currency', 'warranty_provider', 'warranty_ref',
  'warranty_start', 'warranty_end', 'planned_refresh_date', 'notes', 'specs',
  'ip_address', 'mac_address', 'firmware', 'admin_url', 'parent_asset_id', 'default_location_id'];

/**
 * `holder` (editing only): a person to suggest as the holder - the one the
 * device's agent says is signed in, when IT completes a record the agent
 * made. The holder is then offered on the edit form too, and saved through
 * the assign action so the history says who gave it to whom.
 */
export default function AssetForm({ meta, asset = null, holder = null, onClose, onSaved }) {
  const editing = Boolean(asset);
  const suggested = editing && holder && !asset.assigned_user_id ? holder : null;
  const [form, setForm] = useState(editing
    ? { ...fromAsset(asset), assigned_user_id: suggested || asset.assigned_user_id || '' } : blank(meta));
  const [saving, setSaving] = useState(false);
  // What was on the form when it opened: while the fields differ from it,
  // closing or leaving asks first (lib/useUnsavedChanges). Released on save.
  const [opened] = useState(() => JSON.stringify(editing ? fromAsset(asset) : blank(meta)));
  const dirty = !saving && JSON.stringify(form) !== opened;
  const leave = useUnsavedChanges(dirty, { what: 'What you typed about this device' });
  const close = () => leave.guard(onClose);
  const [error, setError] = useState(null);
  // The device this one is plugged into (092): the picker's choice, or the
  // one the record already names.
  const [parent, setParent] = useState(editing && asset.parent_asset_id
    ? { id: asset.parent_asset_id, asset_tag: asset.parent_tag, manufacturer: asset.parent_make, model: asset.parent_model } : null);
  const chooseParent = useCallback((a) => {
    setParent(a);
    setForm((f) => ({ ...f, parent_asset_id: a?.id || '' }));
  }, []);

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  // Default office (104): prefilled from the place it is kept at, unless that
  // is storage (a spare in a cupboard belongs to an office, not that cupboard)
  // - until the person has chosen one themselves.
  const [officeChosen, setOfficeChosen] = useState(false);
  const places = (meta?.locations || []).filter((l) => l.active);
  const isStorage = (id) => ['storage', 'container', 'shelf'].includes(places.find((l) => l.id === id)?.kind);
  const chooseKeptAt = (e) => {
    const id = e.target.value;
    setForm((f) => ({ ...f, location_id: id,
      default_location_id: officeChosen || !id || isStorage(id) ? f.default_location_id : id }));
  };
  const category = useMemo(
    () => (meta?.categories || []).find((c) => c.id === form.asset_category_id),
    [meta, form.asset_category_id]);
  const hasCompanies = Array.isArray(meta?.companies);
  const typed = hasCompanies ? TYPED : TYPED.filter((k) => k !== 'company_id');
  const company = (meta?.companies || []).find((c) => c.id === form.company_id);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    // Every device has a place: "Kept at", or At their home for a person's.
    if (!editing && !form.location_id && !(form.at_home && form.holder.startsWith('u:'))) {
      setError({ text: 'Choose where it is kept. Every device needs a place.', field: 'location_id' });
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (editing) {
        // Only what changed: each changed field is a line in the history,
        // and a form that resent every field would claim nothing changed
        // only by luck.
        const body = {};
        for (const k of typed) {
          const before = k === 'specs' ? JSON.stringify(asset.specs || {})
            : String(fromAsset(asset)[k] ?? '');
          const now = k === 'specs' ? JSON.stringify(form.specs || {}) : String(form[k] ?? '');
          if (before !== now) body[k] = k === 'is_shared' ? Boolean(form[k]) : form[k];
        }
        const giving = suggested && form.assigned_user_id && form.assigned_user_id !== (asset.assigned_user_id || '');
        if (Object.keys(body).length === 0 && !giving) { leave.release(); onClose(); return; }
        saved = Object.keys(body).length
          ? (await axios.put(`${API_BASE}/assets/${asset.id}`, body, contractorAuth())).data : asset;
        if (giving) {
          await axios.post(`${API_BASE}/assets/${asset.id}/assign`,
            { user_id: form.assigned_user_id, reason: 'Completed from the Devices list' }, contractorAuth());
        }
      } else {
        const body = {};
        for (const k of typed) {
          if (k === 'is_shared') body[k] = Boolean(form[k]);
          else if (k === 'specs') body[k] = form.specs || {};
          else if (form[k] !== '' && form[k] !== null) body[k] = form[k];
        }
        // Who has it: a person (assigned_user_id) or a contractor
        // (assigned_contractor_id, 093), from the one With control. At home
        // is a person's: a contractor has no Home node in the register.
        Object.assign(body, holderFields(form.holder));
        if (form.at_home && form.holder.startsWith('u:')) body.at_home = true;
        else if (form.location_id) body.location_id = form.location_id;
        saved = (await axios.post(`${API_BASE}/assets`, body, contractorAuth())).data;
      }
      leave.release();
      onSaved(saved);
    } catch (err) {
      setError({ text: err.response?.data?.error || 'Could not save the device.',
        field: err.response?.data?.field });
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label, props = {}) => (
    <div className="form-group">
      <label htmlFor={`as-f-${k}`}>{label}</label>
      <input id={`as-f-${k}`} value={form[k] ?? ''} onChange={set(k)}
        aria-invalid={error?.field === k ? 'true' : undefined} {...props} />
    </div>
  );

  return (
    <Modal
      wide
      title={editing ? `Edit ${tagText(asset)}` : 'Add a device'}
      subtitle={editing ? 'Holder, place and status are changed from the device’s page.' : null}
      onClose={close}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={close}>Cancel</button>
          <button type="submit" form="as-device-form" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save' : 'Add device'}
          </button>
        </>
      )}
    >
      <UnsavedChangesDialog leave={leave} />
      <form id="as-device-form" onSubmit={submit}>
        {error && <p className="as-error" role="alert">{error.text}</p>}
        <div className="as-form-grid">
          <div className="form-group">
            <label htmlFor="as-f-cat">Category</label>
            <select id="as-f-cat" value={form.asset_category_id} onChange={set('asset_category_id')} required>
              {(meta?.categories || []).filter((c) => c.active || c.id === form.asset_category_id)
                .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {hasCompanies && (
            <div className="form-group">
              <label htmlFor="as-f-company">Company</label>
              <select id="as-f-company" value={form.company_id || ''} onChange={set('company_id')}
                aria-invalid={error?.field === 'company_id' ? 'true' : undefined}>
                <option value="">None</option>
                {meta.companies.filter((c) => !c.archived_at || c.id === form.company_id).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.archived_at ? ' (archived)' : ''}</option>
                ))}
              </select>
              {company && (
                <p style={{ margin: '0.35rem 0 0' }}>
                  <CompanyChip name={company.name} color={company.color} code={company.short_code} />
                </p>
              )}
            </div>
          )}
          {field('asset_tag', editing ? 'Tag' : 'Tag (leave empty to number it)', {
            placeholder: category ? `${category.tag_prefix}-000…` : ''
          })}
          {field('manufacturer', 'Make', { placeholder: 'Dell' })}
          {field('model', 'Model', { placeholder: 'Latitude 5440' })}
          {field('serial_number', 'Serial number')}
          {field('hostname', 'Hostname')}
        </div>

        {editing && suggested && (
          <>
            <p className="as-form-section">Who has it</p>
            <div className="as-form-grid">
              <div className="form-group">
                <label htmlFor="as-f-holder">Assigned to</label>
                <select id="as-f-holder" value={form.assigned_user_id} onChange={set('assigned_user_id')}>
                  <option value="">Nobody — in stock</option>
                  {(meta?.users || []).map((u) => (
                    <option key={u.id} value={u.id}>{u.display_name || u.email}</option>
                  ))}
                </select>
                <p className="as-quiet" style={{ margin: '0.25rem 0 0', fontSize: '0.82rem' }}>
                  Suggested from who its agent says is signed in. Written into its history on save.
                </p>
              </div>
            </div>
          </>
        )}
        <div className="as-form-grid">
          <div className="form-group">
            <label htmlFor="as-f-office">Default office</label>
            <select id="as-f-office" value={form.default_location_id || ''} required={!editing}
              onChange={(e) => { setOfficeChosen(true); set('default_location_id')(e); }}>
              <option value="">{editing ? 'None' : 'Choose a room or desk…'}</option>
              {places.map((l) => <option key={l.id} value={l.id}>{l.path}</option>)}
            </select>
          </div>
        </div>
        {!editing && (
          <>
            <p className="as-form-section">Who has it and where</p>
            <div className="as-form-grid">
              <div className="form-group">
                <label htmlFor="as-f-holder">With</label>
                <HolderSelect id="as-f-holder" meta={meta} value={form.holder} none="Nobody — in stock"
                  onChange={(key) => setForm((f) => ({ ...f, holder: key, at_home: key.startsWith('u:') ? f.at_home : false }))} />
              </div>
              <div className="form-group">
                <label htmlFor="as-f-place">Kept at</label>
                <select id="as-f-place" value={form.location_id} onChange={chooseKeptAt}
                  disabled={form.at_home}
                  aria-invalid={error?.field === 'location_id' || undefined}>
                  <option value="">Choose a place…</option>
                  {(meta?.locations || []).filter((l) => l.active).map((l) => (
                    <option key={l.id} value={l.id}>{l.path}</option>
                  ))}
                </select>
              </div>
              {form.holder.startsWith('u:') && (
                <label className="as-check">
                  <input type="checkbox" checked={form.at_home} onChange={set('at_home')} />
                  At their home (remote worker)
                </label>
              )}
            </div>
          </>
        )}

        <div className="as-form-grid">
          <label className="as-check">
            <input type="checkbox" checked={Boolean(form.is_shared)} onChange={set('is_shared')} />
            Shared equipment — anybody nearby may report it
          </label>
        </div>

        {(category?.spec_schema || []).length > 0 && (
          <>
            <p className="as-form-section">Specification</p>
            <div className="as-form-grid">
              {category.spec_schema.map((f) => (
                <div className="form-group" key={f.key}>
                  <label htmlFor={`as-spec-${f.key}`}>{f.label}</label>
                  <input id={`as-spec-${f.key}`} value={form.specs?.[f.key] ?? ''}
                    onChange={(e) => setForm((x) => ({
                      ...x, specs: { ...(x.specs || {}), [f.key]: e.target.value }
                    }))} />
                </div>
              ))}
            </div>
          </>
        )}

        <p className="as-form-section">Purchase and warranty</p>
        <div className="as-form-grid">
          <div className="form-group">
            <label htmlFor="as-f-own">Ownership</label>
            <select id="as-f-own" value={form.ownership} onChange={set('ownership')}>
              {Object.entries(OWNERSHIP_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          {(meta?.departments || []).length > 0 && (
            <div className="form-group">
              <label htmlFor="as-f-dep">Department</label>
              <select id="as-f-dep" value={form.department_id || ''} onChange={set('department_id')}>
                <option value="">None</option>
                {meta.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          )}
          {field('purchase_date', 'Bought on', { type: 'date' })}
          {field('supplier', 'Supplier')}
          {field('invoice_ref', 'Invoice')}
          {field('cost_ex_vat', 'Cost excl. VAT', { type: 'number', min: 0, step: '0.01' })}
          {field('currency', 'Currency', { maxLength: 3 })}
          {field('warranty_provider', 'Warranty from')}
          {field('warranty_ref', 'Warranty reference')}
          {field('warranty_start', 'Warranty starts', { type: 'date' })}
          {field('warranty_end', 'Warranty ends', { type: 'date' })}
          {field('planned_refresh_date', 'Replace by', { type: 'date' })}
        </div>
        <p className="as-form-section">On the network</p>
        <div className="as-form-grid">
          {field('ip_address', 'IP address', { placeholder: '10.0.0.50' })}
          {field('mac_address', 'MAC address', { placeholder: 'aa:bb:cc:dd:ee:ff' })}
          {field('firmware', 'Firmware')}
          {field('admin_url', 'Admin page', { placeholder: 'https://…', type: 'url' })}
          <AssetPicker value={parent} onChange={chooseParent} asFormGroup label="Plugged into" />
        </div>
        <div className="form-group">
          <label htmlFor="as-f-notes">Description</label>
          <textarea id="as-f-notes" rows={3} value={form.notes || ''} onChange={set('notes')} />
        </div>
      </form>
    </Modal>
  );
}
